import { useEffect, useMemo, useState } from 'react';
import { usePaginatedQuery, useQuery } from 'react-query';
import { getCalendarHistorySelection } from '../../../utils/DateUtils/historyRange';
import { createHistoryTraversal } from '../../../services/HistoryTraversal';
import {
  getDriversHistory,
  getOrderDetails,
  getOrderHistory,
} from '../../../services/DeliveryOperationsPortalService';

export const useDeliveryItem = ({ id }) => {
  const {
    data: delivery,
    isLoading,
    isError,
  } = useQuery(['delivery-item', id], (key, id) => getOrderDetails(id));

  return {
    delivery,
    isLoading,
    isError,
  };
};

export const usePaginatedHistory = ({ page, dateRange }) => {
  const {
    isLoading,
    isError,
    resolvedData: orders,
    isFetching,
    isFetchingMore,
    refetch,
    ...rest
  } = usePaginatedQuery(
    ['order-history', page, dateRange],
    (key, page, dateRange) =>
      getOrderHistory({
        page,
        dateRange,
      }),
    {
      keepPreviousData: false, retry: false,
      refetchOnWindowFocus: false, refetchOnReconnect: false,
    }
  );

  return {
    orders,
    isLoading,
    isFetching,
    isFetchingMore,
    isError,
    refetch,
    ...rest,
  };
};

export const useHistoryTraversal = (dateRange, enabled = true) => {
  const traversal = useMemo(() =>
    createHistoryTraversal(getCalendarHistorySelection(dateRange), getDriversHistory), [dateRange]);
  useEffect(() => {
    if (!enabled) return undefined;
    const unsubscribe = traversal.watchIdentity();
    traversal.fetch();
    return () => { unsubscribe(); traversal.dispose(); };
  }, [traversal, enabled]);
  return traversal;
};

export const usePaginatedDriversHistory = ({ dateRange, traversal: sharedTraversal }) => {
  const local = useHistoryTraversal(dateRange, !sharedTraversal);
  const traversal = sharedTraversal || local;
  const [, update] = useState(0);
  useEffect(() => traversal.subscribe(() => update((value) => value + 1)), [traversal]);
  return { ...traversal.state, traversal, page: traversal.page,
    setPage: (page) => traversal.fetch(page), refetch: () => traversal.retry() };
};
